import { DEATH_CAUSE_LABEL } from "@/constants/uiLabels";
import type { Database } from "@/types/database";
import { compareIsoDates, formatIsoDateDE, isStrictIsoDate } from "@/utils/isoDate";

export type DeathCause = Database["public"]["Enums"]["death_cause"];
export type CaseStatus = Database["public"]["Enums"]["case_status"];

/** Abschluss laut case_status. Ein neuer Enum-Wert muss hier eingeordnet werden. */
const DEATH_REPORT_CLOSED: { [K in CaseStatus]: boolean } = {
  in_care: false,
  hibernating: false,
  released: true,
  deceased: true,
  voided: true,
};

export function isDeathReportClosed(status: CaseStatus): boolean {
  return DEATH_REPORT_CLOSED[status];
}

type DeathCauseNote =
  Database["public"]["Tables"]["care_event_closure_deceased"]["Row"]["death_cause_note"];

export const DEATH_ON_TIME_ZONE = "Europe/Berlin";

const DEATH_CAUSE_ORDER = [
  "unknown",
  "illness",
  "injury_accident",
  "euthanasia",
  "other",
] as const satisfies readonly DeathCause[];

export const DEATH_CAUSE_OPTIONS: { value: DeathCause; label: string }[] =
  DEATH_CAUSE_ORDER.map((value) => ({ value, label: DEATH_CAUSE_LABEL[value] }));

export const DEATH_DATE_FUTURE_ERROR =
  "Das Todesdatum darf nicht in der Zukunft liegen.";
export const DEATH_DATE_BEFORE_FOUND_ERROR =
  "Das Todesdatum darf nicht vor dem Funddatum liegen.";
export const DEATH_DATE_INVALID_ERROR = "Das Todesdatum ist ungültig.";
export const DEATH_CAUSE_REQUIRED_ERROR = "Todesart angeben.";
export const DEATH_CAUSE_PICK_HINT = "Bitte Todesart wählen.";
export const DEATH_CAUSE_DESCRIBE_HINT = "Bitte Todesart beschreiben.";
export const DEATH_NOTE_REQUIRED_ERROR = "Bei Sonstiges ist ein Freitext erforderlich.";

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export function calendarDateInTimeZone(now: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  if (!year || !month || !day) {
    throw new Error("Kalenderdatum nicht ermittelbar.");
  }
  return `${year}-${month}-${day}`;
}

/** Heutiger Kalendertag in Europe/Berlin, vorbelegt wie das Funddatum, aber in der Speicher-Zeitzone. */
export function initialDeathOn(now: Date = new Date()): string {
  return calendarDateInTimeZone(now, DEATH_ON_TIME_ZONE);
}

export function deathCauseLabel(value: string | null | undefined): string | null {
  if (!isDeathCause(value)) return null;
  return DEATH_CAUSE_LABEL[value];
}

export function isDeathCause(value: string | null | undefined): value is DeathCause {
  return DEATH_CAUSE_OPTIONS.some((option) => option.value === value);
}

/** Kalendertag aus YYYY-MM-DD oder aus einem Zeitstempel in Europe/Berlin. */
export function diedAtToIsoDate(value: string | null | undefined): string | null {
  if (value == null || value.trim() === "") return null;
  const trimmed = value.trim();
  if (ISO_DAY.test(trimmed) && isStrictIsoDate(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return calendarDateInTimeZone(parsed, DEATH_ON_TIME_ZONE);
}

export function assessDeathOn(
  value: string | null,
  opts: { today: string; foundOn: string | null }
): { ok: true; date: string | null } | { ok: false; error: string } {
  if (value == null || value.trim() === "") return { ok: true, date: null };
  const trimmed = value.trim();
  if (!ISO_DAY.test(trimmed) || !isStrictIsoDate(trimmed)) {
    return { ok: false, error: DEATH_DATE_INVALID_ERROR };
  }
  if (compareIsoDates(trimmed, opts.today) > 0) {
    return { ok: false, error: DEATH_DATE_FUTURE_ERROR };
  }
  const found = (opts.foundOn ?? "").trim().slice(0, 10);
  if (ISO_DAY.test(found) && isStrictIsoDate(found) && compareIsoDates(trimmed, found) < 0) {
    return { ok: false, error: DEATH_DATE_BEFORE_FOUND_ERROR };
  }
  return { ok: true, date: trimmed };
}

export type ClosureDeceasedEvent = {
  event_type: "closure_deceased";
  death_cause: DeathCause;
  /**
   * Zusätzlich zum Enum, damit der Abschluss auch ohne die Migration
   * (Preview, Outbox, db push nach dem UI-Deploy) die NOT-NULL-Spalte füllt.
   * Bekannte Arten: deutsches Label. Sonstiges: der Freitext.
   */
  death_kind: string;
  death_cause_note: DeathCauseNote;
  died_at: string | null;
  notes: string | null;
  sort_index: 1;
};

/** Label der Todesart; bei Sonstiges der Freitext. Füllt death_kind auch ohne Migration. */
export function deathKindForPayload(cause: DeathCause, note: string): string {
  if (cause === "other") return note.trim();
  return deathCauseLabel(cause) ?? cause;
}

export function assessDeathClosure(input: {
  cause: DeathCause | null;
  note: string;
  diedOn: string | null;
  notes: string;
  foundOn: string | null;
  today: string;
}): { ok: true; event: ClosureDeceasedEvent } | { ok: false; error: string } {
  if (!input.cause) return { ok: false, error: DEATH_CAUSE_REQUIRED_ERROR };
  const note = input.note.trim();
  if (input.cause === "other" && !note) {
    return { ok: false, error: DEATH_NOTE_REQUIRED_ERROR };
  }
  if (input.cause !== "other" && note) {
    return { ok: false, error: "Ein Freitext ist nur bei Sonstiges erlaubt." };
  }
  const date = assessDeathOn(input.diedOn, {
    today: input.today,
    foundOn: input.foundOn,
  });
  if ("error" in date) return { ok: false, error: date.error };
  return {
    ok: true,
    event: {
      event_type: "closure_deceased",
      death_cause: input.cause,
      death_kind: deathKindForPayload(input.cause, note),
      death_cause_note: input.cause === "other" ? note : null,
      died_at: date.date,
      notes: input.notes.trim() || null,
      sort_index: 1,
    },
  };
}

/**
 * Chronikzeile. Ohne death_cause keine Todesart.
 * Ein alter death_kind-Freitext erscheint dann als „Angabe (alt)“.
 */
export function formatClosureDeceasedLine(ev: {
  death_cause?: string | null;
  death_cause_note?: string | null;
  died_at?: string | null;
  death_kind?: string | null;
}): string {
  const lines = ["Verstorben"];
  const iso = diedAtToIsoDate(ev.died_at);
  if (iso) lines.push(`Todesdatum: ${formatIsoDateDE(iso)}`);
  const label = deathCauseLabel(ev.death_cause);
  if (!label) {
    const legacy = ev.death_kind?.trim();
    if (legacy) lines.push(`Angabe (alt): ${legacy}`);
    return lines.join("\n");
  }
  if (ev.death_cause === "other") {
    const note = ev.death_cause_note?.trim();
    lines.push(note ? `Todesart: Sonstiges (${note})` : "Todesart: Sonstiges");
  } else {
    lines.push(`Todesart: ${label}`);
  }
  return lines.join("\n");
}
